import React, { useCallback } from 'react';
import Paragraph from '@tiptap/extension-paragraph';
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import { FaTrash, FaGripVertical } from 'react-icons/fa';

const DraggableParagraphView = ({ node, getPos, editor }) => {
  // Any paragraph inside a list item sits in a flex row (list content + icons).
  // `inline-flex` / `w-fit` there refuses the remaining width and forces early
  // wraps — use full-width flex + min-w-0 so text uses the real line width.
  let insideListItem = false;
  try {
    if (editor && getPos && typeof getPos === 'function') {
      const pos = getPos();
      if (pos !== null && pos !== undefined) {
        const { doc } = editor.state;
        if (pos >= 0 && pos <= doc.content.size) {
          const $pos = doc.resolve(pos);
          for (let d = $pos.depth; d > 0; d--) {
            if ($pos.node(d).type.name === 'listItem') {
              insideListItem = true;
              break;
            }
          }
        }
      }
    }
  } catch (_) {
    /* ignore — fall back to outside-list layout */
  }

  const handleDelete = useCallback(() => {
    if (!getPos || typeof getPos !== 'function' || !editor) return;
    try {
      const pos = getPos();
      if (pos === null || pos === undefined) return;
      const { doc } = editor.state;
      const end = pos + node.nodeSize;
      if (pos < 0 || end > doc.content.size) return;
      editor.chain().focus().deleteRange({ from: pos, to: end }).run();
    } catch (_) {
      /* position may be stale after doc change; ignore */
    }
  }, [editor, getPos, node.nodeSize]);

  const isEmpty = !node.content || node.content.size === 0;

  /* List rows own hover via DraggableListItem — skip paragraph hover/group to avoid double highlight */
  const wrapperClass = [
    "items-center gap-3 draggable-paragraph rounded-md px-1 -mx-1",
    insideListItem ? "flex w-full min-w-0 max-w-full" : "inline-flex",
    !insideListItem && "group hover:bg-gray-50 dark:hover:bg-gray-700/60",
    node.attrs.class,
  ]
    .filter(Boolean)
    .join(" ");

  // Outside lists: keep w-fit for short lines (drop target to the right), but empty
  // paragraphs need a real min width or the blinking caret is invisible.
  const contentClass = insideListItem
    ? "draggable-paragraph-content min-w-0 flex-1 cursor-text"
    : [
        "draggable-paragraph-content max-w-full cursor-text pe-2 [&>p]:max-w-full",
        isEmpty ? "min-w-[1em] w-fit" : "w-fit [&>p]:w-fit",
      ].join(" ");

  return (
    <NodeViewWrapper
      className={wrapperClass}
      onDragStart={(e) => {
        // Only allow drag if starting from drag handle
        if (!e.target.closest('[data-drag-handle]')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <NodeViewContent
        className={contentClass}
        onDragStart={(e) => {
          // Prevent drag when starting from content area
          e.preventDefault();
          e.stopPropagation();
        }}
      />
      {!insideListItem && (
        <div className="flex w-14 flex-shrink-0 items-center justify-end gap-1 ps-2 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
          <button
            onClick={handleDelete}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
            title="Delete"
            contentEditable={false}
          >
            <FaTrash className="w-3 h-3 text-gray-600 dark:text-gray-400" />
          </button>
          <div
            data-drag-handle
            draggable="true"
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded cursor-grab active:cursor-grabbing"
            title="Drag"
            contentEditable={false}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'move';
            }}
          >
            <FaGripVertical className="w-3 h-3 text-gray-600 dark:text-gray-400" />
          </div>
        </div>
      )}
    </NodeViewWrapper>
  );
};

// Extend default Paragraph so setParagraph / keyboard shortcuts keep working.
export const DraggableParagraph = Paragraph.extend({
  name: 'paragraph',
  priority: 1000,
  draggable: true,

  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute('class') || null,
        renderHTML: (attrs) => (attrs.class ? { class: attrs.class } : {}),
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(DraggableParagraphView);
  },
});
